import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FiSearch, FiCopy } from "react-icons/fi";
import { FaPaperPlane } from "react-icons/fa";
import DOMPurify from "dompurify";
import { motion } from "framer-motion";
import { sendProductToTelegram } from "../services/telegramService";
import ChatButton from "../components/ChatButton";

const API_KEY = "d0d0e4b5f11eccb0cab298bbb66068273ab5a3d145a993577cc9a94a761273f6";
const API_PRODUCTS = "https://xalavach.ru/api/products.php";
const API_CATEGORIES = "https://xalavach.ru/api/categories.php";

export default function Search({ telegramUserId }) {
  const [query, setQuery] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [foundCategory, setFoundCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [usageVisible, setUsageVisible] = useState({});

  const handleSearch = async () => {
    if (!query.trim()) return;

    setIsLoading(true);
    try {
      const categoryResponse = await axios.get(API_CATEGORIES, {
        headers: { Authorization: `Bearer ${API_KEY}` },
      });
      
      const matchedCategory = categoryResponse.data.find((category) => 
        category.name.toLowerCase().includes(query.toLowerCase())
      );

      setFoundCategory(matchedCategory);

      const productResponse = await axios.get(API_PRODUCTS, {
        headers: { Authorization: `Bearer ${API_KEY}` },
      });
      
      const searchResults = productResponse.data.filter((product) => 
        product.name.toLowerCase().includes(query.toLowerCase()) || 
        (matchedCategory && product.category_id === matchedCategory.id)
      );

      setFilteredProducts(searchResults);
      setSearchActive(true);
    } catch (error) {
      console.error("Ошибка при поиске:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A1A2E] to-[#16213E] text-white flex flex-col pb-24">
      <div className="w-full px-4 pt-20 pb-3 flex justify-center">
        <div className="flex items-center bg-[#222A3A] px-4 py-2 rounded-lg shadow-md w-full max-w-md">
          <input
            type="text"
            className="bg-transparent text-white w-full focus:outline-none placeholder-gray-400"
            placeholder="🔎 Введите запрос..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button
            onClick={handleSearch}
            className="ml-3 p-2 bg-blue-600 rounded-full hover:bg-blue-700 transition shadow-md"
          >
            <FiSearch className="text-white text-xl" />
          </button>
        </div>
      </div>
      
      <div className="px-4 pb-10 flex-1">
        {isLoading && (
          <motion.div className="flex flex-col items-center justify-center mt-10">
            <div className="bg-[#222A3A] bg-opacity-80 px-6 py-4 rounded-lg shadow-md text-center">
              <h2 className="text-xl font-semibold text-gray-300">⏳ Ищем...</h2>
            </div>
          </motion.div>
        )}
        
        {searchActive && foundCategory && (
          <motion.div className="mb-6 bg-gradient-to-br from-[#232A3B] to-[#1E2A47] p-5 rounded-xl shadow-lg text-center">
            <h2 className="text-lg sm:text-xl font-bold text-blue-400">🔍 Категория: {foundCategory.name}</h2>
          </motion.div>
        )}
        
        {searchActive && filteredProducts.length === 0 && !isLoading && (
          <motion.div className="flex flex-col items-center justify-center mt-10">
            <div className="bg-[#222A3A] bg-opacity-90 px-8 py-5 rounded-xl shadow-xl text-center">
              <h2 className="text-2xl font-semibold text-white">😞 Простите...</h2>
              <h3 className="text-lg text-gray-300 mt-2">По вашему запросу ничего не найдено.</h3>
            </div>
          </motion.div>
        )}
		
		{searchActive && filteredProducts.length > 0 && (
          <div className="grid grid-cols-1 gap-4">
            {filteredProducts.map((product) => (
              <motion.div key={product.id} className="bg-gradient-to-b from-[#1B2433] to-[#2D415A] rounded-xl p-4 mx-auto border border-gray-600">
                {/* Основное изображение */}
                <div className="rounded-t-xl overflow-hidden shadow-md">
                  <img
                    src={`https://xalavach.ru/api/image.php?file=${product.main_image}`}
                    alt={product.name}
                    className="w-full object-cover"
                  />
                </div>

                {/* Блок с названием, категорией, датой */}
                <div className="p-4 flex justify-between items-center">
                  <h2 className="text-lg font-bold">{product.name}</h2>
                  <div className="bg-yellow-400 text-black px-3 py-1 rounded-lg shadow-md text-sm">
                    {product.expiry_date ? `До ${formatDate(product.expiry_date)}` : "Без срока"}
                  </div>
                </div>

                {/* Детали карточки */}
                <div className="p-5 bg-gradient-to-b from-[#253547] to-[#34495E] rounded-xl shadow-md border border-gray-600">
                  <p className="text-lg text-gray-300">{product.description}</p>

                  {/* Промокод */}
                  <div className="mt-5 p-4 bg-gray-900 text-white rounded-xl flex items-center justify-between shadow-lg border border-gray-700">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-400">Твой промокод:</span>
                      <span className="font-bold text-xl tracking-wide bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600">
                        {product.promo_code}
                      </span>
                    </div>
                    <button
                      className="bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white px-4 py-2 rounded-full flex items-center transition duration-200 shadow-md"
                      onClick={() => navigator.clipboard.writeText(product.promo_code)}
                    >
                      <FiCopy size={22} />
                    </button>
                  </div>

                  {/* Кнопка "Скопировать и перейти на сайт" */}
                  {product.website_link && (
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(product.promo_code);
                        window.open(product.website_link, "_blank");
                      }}
                      className="w-full mt-4 bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800 
                                 text-white py-3 rounded-lg text-lg font-semibold transition duration-200"
                    >
                      Скопировать и перейти на сайт
                    </button>
                  )}

                  {/* Кнопка "Как использовать?" */}
					<button
					  onClick={() => setUsageVisible(prev => ({ ...prev, [product.id]: !prev[product.id] }))}
					  className="w-full mt-4 bg-blue-500 text-white py-3 rounded-lg shadow-lg text-lg font-semibold transition duration-200 hover:bg-blue-700"
					>
					  Как использовать?
					</button>

					{usageVisible[product.id] && (
					  <div className="mt-3 bg-gray-900 p-4 rounded-lg shadow-md text-gray-300 overflow-hidden break-words">
						<p className="whitespace-pre-line break-words text-sm leading-relaxed overflow-x-hidden" 
						   style={{ wordBreak: "break-word" }}
						   dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.usage_rules) }} />
					  </div>
					)}


                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* ✅ Кнопка "Отправить в чат" */}
      {searchActive && filteredProducts.length > 0 && (
     <ChatButton brandProducts={filteredProducts} telegramUserId={telegramUserId} />

      )}
    </div>
  );
}
function formatDate(dateStr) {
  const months = [
    "января", "февраля", "марта", "апреля", "мая", "июня",
    "июля", "августа", "сентября", "октября", "ноября", "декабря"
  ];
  const [year, month, day] = dateStr.split("-");
  return `${parseInt(day)} ${months[parseInt(month) - 1]}`;
}